import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppCheckService } from './app-check.service';
import { environment } from '../../../../../environments/environment'
import { version, build } from '../../../../../buildVersion'

export interface AnalyticsEvent {
  action: string;
  contextId?: string | null;
  contentTypeId?: string | null;
  linkURL?: string;
  platform?: string;
  appBuild?: number | string;
  appVersion?: number | string;
  passTemplateId?: string | null;
  timeStamp?: any;
  passId?: string | null;
  percentage?: number;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private hostname = environment.urlHostName;
  private appBuild = build
  private version = version

  constructor(
    private http: HttpClient,
    private appCheck: AppCheckService
  ) {}

  // helpers

  private toFsTimestamp(ms: number) {
    const seconds = Math.floor(ms / 1000);
    const nanoseconds = (ms % 1000) * 1e6;
    return {
      _seconds: seconds,
      _nanoseconds: nanoseconds,
    };
  }

  private getPlatform(): string {
    let platform = 'Unknown';

    const uaData = (navigator as any).userAgentData;
    if (uaData?.platform) {
      platform = uaData.platform;
    } else {
      const ua = navigator.userAgent || '';
      if (/windows/i.test(ua)) platform = 'Windows';
      else if (/mac/i.test(ua)) platform = 'macOS';
      else if (/iphone|ipad|ipod/i.test(ua)) platform = 'iOS';
      else if (/android/i.test(ua)) platform = 'Android';
    }

    if (platform === 'iOS' || platform === 'Android') {
      platform += ' Web';
    }

    return platform;
  }

  private baseEvent(doc: any): Partial<AnalyticsEvent> {
    const platform = this.getPlatform();

    return {
      contextId: doc?.contextId ?? null,
      contentTypeId: doc?.contentTypeId ?? null,
      platform,
      appBuild: this.appBuild ?? null,
      appVersion: this.version ?? null,
      passTemplateId: doc?.passTemplateId ?? null,
      passId: doc?.tridyId ?? null,
    };
  }

  // public api

  async send(events: AnalyticsEvent[]): Promise<void> {
    if (!events.length) return;

    const url = `https://${this.hostname}/v1/analytics`;
    const ctx = events[0]?.contextId ?? null;

    // AppCheck
    const token = await this.appCheck.getToken();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json;charset=UTF-8',
    });

    if (ctx) {
      headers = headers.set('Context-Id', String(ctx));
    }
    if (token) {
      headers = headers.set('X-Firebase-AppCheck', token);
    } else {
      console.warn('No App Check token for analytics!');
    }

    // optional logging
    console.log('POST /v1/analytics – events:', events);

    this.http.post(url, { analytics: events }, { headers }).subscribe({
      next: (res) => {
        // Optional: you can remove this in production if you want it totally silent
        console.log('Analytics POST success:', res);
      },
      error: (err) => {
        if (err.status === 401) {
          // Known behavior – backend returns 401 but may still process the event.
          console.warn('Analytics POST got 401 (likely AppCheck/auth issue). Ignoring on client.', err);
          return;
        }

        console.error('Analytics POST failed:', err);
      },
    });

  }

  // event helpers

  /**
   * pass.opened
   */
  async trackPassOpened(doc: any) {
    if (!doc) return;

    const base = this.baseEvent(doc);
    const event: AnalyticsEvent = {
      ...base,
      action: 'pass.opened',
      linkURL: window.location.href,
      timeStamp: this.toFsTimestamp(Date.now()),
    };

    await this.send([event]);
  }

  /**
   * pass.closed
   */
  async trackPassClosed(doc: any) {
    if (!doc) return;

    const base = this.baseEvent(doc);
    const event: AnalyticsEvent = {
      ...base,
      action: 'pass.closed',
      linkURL: window.location.href,
      timeStamp: this.toFsTimestamp(Date.now()),
    };

    await this.send([event]);
  }

  /**
   * pass.link.opened
   */
  async trackLinkOpened(doc: any, href: string) {
    if (!doc) return;

    const base = this.baseEvent(doc);
    const event: AnalyticsEvent = {
      ...base,
      action: 'pass.link.opened',
      linkURL: href,
      timeStamp: this.toFsTimestamp(Date.now()),
    };

    await this.send([event]);
  }

  /**
   * pass.viewed percentage (0..100, only send on certain steps)
   */
  async trackPassViewed(doc: any, percentage: number) {
    if (!doc) return;

    const base = this.baseEvent(doc);
    const event: AnalyticsEvent = {
      ...base,
      action: 'pass.viewed',
      linkURL: window.location.href,
      timeStamp: this.toFsTimestamp(Date.now()),
      percentage,
    };

    await this.send([event]);
  }

  /**
   * TODO: SHARE BUTTON TEMPLATE MISSING
   * pass.shared
   */
  async trackPassShared(doc: any) {
    if (!doc) return;

    const base = this.baseEvent(doc);
    const event: AnalyticsEvent = {
      ...base,
      action: 'pass.shared',
      linkURL: window.location.href,
      timeStamp: this.toFsTimestamp(Date.now()),
    };

    await this.send([event]);
  }
}
